import React, { ReactNode } from 'react';

/**
 * 通用区域组件属性
 */
export interface SectionProps {
  id?: string;                // 用于锚点定位的ID
  title?: string;             // 区域标题
  subtitle?: string;          // 区域副标题
  children: ReactNode;        // 区域内容
  className?: string;         // 自定义类名
  background?: 'white' | 'background' | 'secondary' | 'primary'; // 背景颜色
  titleAlignment?: 'left' | 'center' | 'right'; // 标题对齐方式
  spacing?: 'small' | 'medium' | 'large'; // 内边距大小
  titleClassName?: string;    // 标题自定义类名
  subtitleClassName?: string; // 副标题自定义类名
  contentClassName?: string;  // 内容区域自定义类名
}

/**
 * 通用区域组件
 * 用于封装网站中的各个内容区块，保持统一的样式和结构
 * @param {SectionProps} props - 组件属性
 * @returns {JSX.Element} 渲染的React组件
 */
const Section: React.FC<SectionProps> = ({ 
  id,
  title, 
  subtitle, 
  children, 
  className = '',
  background = 'white',
  titleAlignment = 'center',
  spacing = 'medium',
  titleClassName = '',
  subtitleClassName = '',
  contentClassName = ''
}) => {
  // 根据背景类型设置背景样式
  const getBgClass = () => {
    switch(background) {
      case 'background': return 'bg-background';
      case 'secondary': return 'bg-secondary text-white';
      case 'primary': return 'bg-primary text-white';
      default: return 'bg-white';
    }
  };
  
  // 根据间距大小设置内边距
  const getSpacingClass = () => {
    switch(spacing) {
      case 'small': return 'py-8 md:py-12';
      case 'large': return 'py-20 md:py-28';
      default: return 'py-16 md:py-24';
    }
  };
  
  // 根据对齐方式设置标题对齐
  const getAlignmentClass = () => {
    switch(titleAlignment) {
      case 'left': return 'text-left';
      case 'right': return 'text-right';
      default: return 'text-center';
    }
  };
  
  return (
    <section 
      id={id} 
      className={`${getBgClass()} ${getSpacingClass()} relative overflow-hidden ${className}`}
    >
      <div className="max-w-7xl mx-auto px-6">
        {(title || subtitle) && (
          <div className={`${getAlignmentClass()} mb-16`}>
            {title && (
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${titleClassName}`}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`max-w-2xl mx-auto ${titleAlignment === 'center' ? 'mx-auto' : ''} ${background === 'white' || background === 'background' ? 'text-gray-600' : 'text-gray-200'} ${subtitleClassName}`}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className={contentClassName}>
          {children}
        </div>
      </div>
    </section>
  );
};

export default Section;